import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import "./BioPopupModal.css";

const BioPopupModal = ({
  show,
  onHide,
  bio = "",
  mode = "view",
  onSave,
  maxLength = 5000,
  isSaving = false,
}) => {
  const [bioText, setBioText] = useState(bio);
  const modalRef = useRef(null);
  const triggerRef = useRef(null);
  const textareaRef = useRef(null);

  // Sync internal state when bio prop changes or modal opens
  useEffect(() => {
    if (show) {
      // Convert <br> tags back to newlines for editing
      const textForEdit = bio ? bio.replace(/<br\s*\/?>/gi, '\n') : '';
      setBioText(textForEdit);
    }
  }, [show, bio]);

  // Store the trigger element when modal opens
  useEffect(() => {
    if (show) {
      triggerRef.current = document.activeElement;
    }
  }, [show]);

  // Focus management: move focus into modal on open
  const handleEntered = useCallback(() => {
    if (mode === "edit" && textareaRef.current) {
      textareaRef.current.focus();
    } else if (modalRef.current) {
      modalRef.current.focus();
    }
  }, [mode]);

  // Return focus to trigger element on close
  const handleExited = useCallback(() => {
    if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, []);

  const handleTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setBioText(value);
    }
  };

  const handleSave = () => {
    if (onSave && bioText.trim().length > 0) {
      // Convert newlines to <br> tags for backend storage
      const htmlBio = bioText.replace(/\n/g, '<br>');
      onSave(htmlBio);
    }
  };

  const handleCancel = () => {
    setBioText(bio);
    onHide();
  };

  const remainingChars = maxLength - bioText.length;
  const isAtLimit = remainingChars <= 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      dialogClassName="bio-popup-modal"
      aria-labelledby="bio-popup-modal-title"
      role="dialog"
      enforceFocus
      restoreFocus
      onEntered={handleEntered}
      onExited={handleExited}
    >
      <div ref={modalRef} tabIndex={-1}>
        <Modal.Header closeButton className="bio-popup-modal-header">
          <Modal.Title
            id="bio-popup-modal-title"
            className="bio-popup-modal-title"
          >
            {mode === "edit" ? "Edit Biography" : "Agent Biography"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bio-popup-modal-body">
          {mode === "view" ? (
            <div className="bio-popup-view-content" aria-live="polite">
              <div className="bio-popup-text" dangerouslySetInnerHTML={{ __html: bio }} />
            </div>
          ) : (
            <div className="bio-popup-edit-content">
              <Form.Group controlId="bioTextarea">
                <Form.Label className="visually-hidden">
                  Biography text
                </Form.Label>
                <Form.Control
                  as="textarea"
                  ref={textareaRef}
                  value={bioText}
                  onChange={handleTextChange}
                  maxLength={maxLength}
                  rows={10}
                  className="bio-popup-textarea"
                  placeholder="Enter your biography..."
                  aria-describedby="bio-char-count"
                />
              </Form.Group>
              <div
                id="bio-char-count"
                className={`bio-popup-char-count ${
                  isAtLimit ? "bio-popup-char-limit" : ""
                }`}
                aria-live="polite"
              >
                {isAtLimit ? (
                  <span className="bio-popup-limit-message">
                    Character limit reached ({maxLength}/{maxLength})
                  </span>
                ) : (
                  <span>
                    {remainingChars} character{remainingChars !== 1 ? "s" : ""}{" "}
                    remaining
                  </span>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="bio-popup-modal-footer">
          {mode === "view" ? (
            <Button
              variant="secondary"
              onClick={onHide}
              className="bio-popup-close-btn"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleCancel}
                className="bio-popup-cancel-btn"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                className="bio-popup-save-btn"
                disabled={isSaving || bioText.trim().length === 0}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </>
          )}
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default BioPopupModal;
